/**
 * HouseholdProducts orchestrator — web-layer glue.
 *
 * Deferred from T6 (T6 only shipped the shopping-lists orchestrator); picked
 * up here in T8.3.
 *
 * Pattern per action mirrors `shopping-lists-orchestrator.ts` exactly:
 *
 *   (1) call the pure reducer from @shopista/shared/stores
 *   (2) commit the next state to the Zustand container
 *   (3) persist the relevant row via `adapters.storage`
 *   (4) talk to the server via `adapters.api.HouseholdProducts`
 *
 * The local commit is optimistic — the UI gets the change immediately. On
 * server error we roll back via the complementary reducer action and
 * re-throw for the caller to surface.
 *
 * Storage row shape note: the UI state holds `HouseholdProductWithDetails` (carrying
 * category_name, linked_offer_count, etc. — that is what list UIs need and
 * what legacy also cached). Storage holds the base `HouseholdProduct` row (the
 * join fields are computed server-side). The orchestrator strips detail
 * fields before writing to storage.
 */

import type { QueryClient } from '@tanstack/react-query';
import { keys } from '@shopista/shared/api/hooks';
import {
  addProduct,
  removeProduct,
  updateProduct,
  type Adapters,
} from '@shopista/shared/stores';
import type {
  CreateHouseholdProductRequest,
  HouseholdProduct,
  HouseholdProductWithDetails,
  UpdateHouseholdProductRequest,
} from '@shopista/shared/types';
import type { RootState, RootStore } from './root-store';

export interface HouseholdProductsOrchestrator {
  /**
   * Add a product. Caller constructs the optimistic `HouseholdProductWithDetails`
   * row (with a client-generated id); orchestrator applies it locally,
   * persists, then fires the POST. On success it reconciles with the
   * server-returned id if they differ.
   */
  handleAddProduct(input: {
    optimistic: HouseholdProductWithDetails;
    request: CreateHouseholdProductRequest;
  }): Promise<HouseholdProductWithDetails>;

  /**
   * Update a product. Changes are applied to the UI row immediately, then
   * persisted and PATCHed. The server response is merged back so any
   * recomputed detail fields (e.g. linked_offer_count) replace local guesses.
   */
  handleUpdateProduct(input: {
    productId: string;
    changes: Partial<HouseholdProductWithDetails>;
    request: UpdateHouseholdProductRequest;
  }): Promise<HouseholdProductWithDetails>;

  /**
   * Remove a product. Local + storage removal is immediate; DELETE fires
   * next. Rolls back on server error.
   */
  handleRemoveProduct(input: { productId: string }): Promise<void>;

  /**
   * Bulk hydrate — used at boot and after a resync to replace the full set.
   * Pure reducer call (no optimistic side effects), no storage round-trip
   * here: storage is the caller's concern (sync service seeds it directly).
   */
  handleSetProducts(input: { products: HouseholdProductWithDetails[] }): void;
}

export function createHouseholdProductsOrchestrator(
  store: RootStore,
  adapters: Adapters,
  queryClient?: QueryClient
): HouseholdProductsOrchestrator {
  function withSlice<T>(fn: (state: RootState) => T): T {
    return fn(store.getState());
  }

  function invalidate(): void {
    if (!queryClient) return;
    void queryClient.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
  }

  return {
    async handleAddProduct({ optimistic, request }) {
      // (1) pure reducer
      const current = withSlice((s) => s.HouseholdProducts);
      const next = addProduct(current, { product: optimistic });
      // (2) commit
      store.setState({ HouseholdProducts: next });
      // (3) persist base row
      await adapters.storage.putProduct(stripDetails(optimistic));
      // (4) server
      try {
        const serverProduct = await adapters.api.HouseholdProducts.create(request);
        // Reconcile id if server assigned a different one.
        if (serverProduct.id !== optimistic.id) {
          const after = withSlice((s) => s.HouseholdProducts);
          const afterRemove = removeProduct(after, { productId: optimistic.id });
          const afterAdd = addProduct(afterRemove, { product: serverProduct });
          store.setState({ HouseholdProducts: afterAdd });
          await adapters.storage.deleteProduct(optimistic.id);
          await adapters.storage.putProduct(stripDetails(serverProduct));
        } else {
          // Server kept the id but may have filled in defaults — merge.
          const after = withSlice((s) => s.HouseholdProducts);
          store.setState({
            HouseholdProducts: updateProduct(after, {
              productId: serverProduct.id,
              changes: serverProduct,
            }),
          });
          await adapters.storage.putProduct(stripDetails(serverProduct));
        }
        invalidate();
        return serverProduct;
      } catch (err) {
        const after = withSlice((s) => s.HouseholdProducts);
        store.setState({
          HouseholdProducts: removeProduct(after, { productId: optimistic.id }),
        });
        await adapters.storage
          .deleteProduct(optimistic.id)
          .catch(() => {});
        throw err;
      }
    },

    async handleUpdateProduct({ productId, changes, request }) {
      const before = withSlice((s) => s.HouseholdProducts);
      const original = before.products.find((p) => p.id === productId);
      const next = updateProduct(before, { productId, changes });
      store.setState({ HouseholdProducts: next });
      await adapters.storage.updateProduct(productId, stripDetails(changes));
      try {
        const serverProduct = await adapters.api.HouseholdProducts.update(
          productId,
          request
        );
        const afterServer = withSlice((s) => s.HouseholdProducts);
        store.setState({
          HouseholdProducts: updateProduct(afterServer, {
            productId,
            changes: serverProduct,
          }),
        });
        await adapters.storage.putProduct(stripDetails(serverProduct));
        invalidate();
        return serverProduct;
      } catch (err) {
        if (original) {
          const afterFail = withSlice((s) => s.HouseholdProducts);
          store.setState({
            HouseholdProducts: updateProduct(afterFail, {
              productId,
              changes: original,
            }),
          });
          await adapters.storage
            .putProduct(stripDetails(original))
            .catch(() => {});
        }
        throw err;
      }
    },

    async handleRemoveProduct({ productId }) {
      const before = withSlice((s) => s.HouseholdProducts);
      const original = before.products.find((p) => p.id === productId);
      store.setState({ HouseholdProducts: removeProduct(before, { productId }) });
      await adapters.storage.deleteProduct(productId);
      try {
        await adapters.api.HouseholdProducts.delete(productId);
        invalidate();
      } catch (err) {
        if (original) {
          const afterFail = withSlice((s) => s.HouseholdProducts);
          store.setState({
            HouseholdProducts: addProduct(afterFail, { product: original }),
          });
          await adapters.storage
            .putProduct(stripDetails(original))
            .catch(() => {});
        }
        throw err;
      }
    },

    handleSetProducts({ products }) {
      store.setState({
        HouseholdProducts: {
          ...store.getState().HouseholdProducts,
          products,
        },
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strip `HouseholdProductWithDetails` join fields down to the storage row shape.
 * The detail fields (category_name, linked_offer_count, default_offer_*) are
 * derived server-side from other tables; storing them would duplicate state.
 */
function stripDetails(
  row: HouseholdProductWithDetails | Partial<HouseholdProductWithDetails>
): HouseholdProduct {
  const {
    category_name: _cn,
    linked_offer_count: _loc,
    favorite_offer_count: _foc,
    default_offer_id: _doid,
    default_offer_title: _dot,
    default_offer_price: _dop,
    default_store_name: _dsn,
    ...base
  } = row as HouseholdProductWithDetails;
  return base as HouseholdProduct;
}
